// Consumer auth state: token + profile (`me`). Lightweight context.

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import {
  consumerApi, getToken, setToken, clearToken, isFlowActive, isGuestToken, isStaffSession,
  CONSUMER_TOKEN_KEY, UNAUTHORIZED_EVENT,
} from '../api/consumerClient';

const AuthCtx = createContext(null);

// Is this consumer a guest? Trusts the server's explicit `is_guest` flag
// (publicConsumer). Falls back to the historical "mobile is not a real number"
// test only for a legacy token whose payload predates the flag — an email
// account has NO mobile, so the old regex alone would wrongly flag it a guest
// and force-log it out.
const isGuestConsumer = (c) => {
  if (!c) return false;
  if (typeof c.is_guest === 'boolean') return c.is_guest;
  return !/^[0-9]/.test(c.mobile || '');
};

// On page load / refresh. This module runs at import time — ONCE per full browser
// load, and NOT on in-app (React Router) navigation, which never re-imports it.
//
// A REAL session survives a refresh. How long it lasts is the SERVER's call: each
// sign-in token carries an expiry (CONSUMER_SESSION_MINUTES), the server refuses
// it afterwards, and refresh() below drops a token /me no longer accepts. The app
// keeps no clock of its own.
//
// A GUEST token never survives a refresh — guests were always ephemeral
// (clearGuestSession) — EXCEPT while a flow is in progress: the conversational
// flow runs as a guest until the borrower registers, and the token is the one
// thing that must survive a refresh for it to resume. `isFlowActive` is set while
// a wizard is open and cleared when it finishes or is closed.
if (!isFlowActive() && isGuestToken()) {
  clearToken();
}

export function ConsumerAuthProvider({ children }) {
  const [consumer, setConsumer] = useState(null);
  const [loading, setLoading] = useState(true);
  // Start UNauthenticated and let refresh() confirm via /me. This avoids a flash
  // of the user chip on load (esp. for a leftover guest token) — the nav only
  // shows a chip once we KNOW it's a real, non-guest consumer.
  const [authed, setAuthed] = useState(false);
  // Set only when a live session was CUT SHORT by a 401, so the login screen can
  // explain itself. Cleared on a successful sign-in.
  const [expired, setExpired] = useState(false);
  // When the server says this session ends (ms), from /me's session_expires_at.
  const [sessionEndsAt, setSessionEndsAt] = useState(null);
  // Set when ANOTHER tab of this browser logged out (BUG-054), so the login
  // screen can say why this one went back to it. Cleared on a sign-in.
  const [signedOutElsewhere, setSignedOutElsewhere] = useState(false);
  // `authed` for the storage listener below, which is registered once.
  const authedRef = useRef(false);
  useEffect(() => { authedRef.current = authed; }, [authed]);

  const refresh = useCallback(async () => {
    if (!getToken()) { setConsumer(null); setAuthed(false); setSessionEndsAt(null); setLoading(false); return; }
    try {
      const { data } = await consumerApi.me();
      const c = data.consumer || null;
      const ends = data.session_expires_at ? Date.parse(data.session_expires_at) : NaN;
      setSessionEndsAt(Number.isFinite(ends) ? ends : null);
      // A guest must NEVER persist across a page LOAD. If a leftover guest token
      // surfaces here, drop it so the nav shows a clean, logged-out home. Uses the
      // server's is_guest flag so email accounts (no mobile) aren't misflagged.
      //
      // BUT NOT WHILE A FLOW IS RUNNING. The conversational flow legitimately runs
      // as a GUEST until the borrower registers (the whole "answer first, sign in
      // to see your result" model). TrackMyLoansFlow calls refreshAuth() mid-flow
      // (in onStart, and the watchdog), and clearing the guest token there wiped
      // the session the flow was using → the next flow call 401'd → the borrower
      // hung on "Setting up…" or bounced. So while a flow is active, keep the
      // guest token; just don't treat the guest as signed in (no user chip).
      const guest = !!c && isGuestConsumer(c);
      if (guest && !isFlowActive()) {
        clearToken();
        setConsumer(null);
        setAuthed(false);
      } else if (guest) {
        // Flow in progress: keep the token, but a guest is not "authed".
        setConsumer(c);
        setAuthed(false);
      } else {
        setConsumer(c);
        setAuthed(true);
      }
    } catch {
      clearToken();
      setConsumer(null);
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // A 401 on any authenticated call ends the session. The axios layer clears the
  // token and fires this; flipping `authed` here is what actually moves the user,
  // because ProtectedRoute reads it — and it carries the page they were on, so
  // signing back in returns them there instead of dumping them on the home page.
  //
  // `expired` distinguishes "your session ran out" from "you opened /login" so the
  // login screen can say which it was; a silent bounce out of a half-finished task
  // reads like the app crashed.
  useEffect(() => {
    const onUnauthorized = () => {
      setConsumer(null);
      setAuthed(false);
      setLoading(false);
      setExpired(true);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  // ANOTHER TAB LOGGED OUT, OR IN AS SOMEONE ELSE (BUG-054).
  //
  // The session token is shared by every tab (localStorage), and logging out
  // revokes it on the server and clears it — but only the tab that did it
  // reacted. The others kept the dashboard and the borrower's data on screen
  // until their next call happened to 401, which on a shared device is exactly
  // the exposure a log out is for. Every other tab hears the change as a
  // 'storage' event:
  //   · token gone (or storage cleared) → signed out here too, at once, with no
  //     logout call (the other tab already revoked it); ProtectedRoute then
  //     unmounts the dashboard and its data, and /login says why;
  //   · a DIFFERENT real token → drop to signed-out FIRST, so account A's pages
  //     never render under account B's token, then ask /me who it is now;
  //   · a guest token (a free flow starting elsewhere) → ignored, so a guest
  //     flow is never cleared from under itself.
  // A staff tab ("Open borrower account") runs on its own sessionStorage token
  // and ignores all of this — what the borrower does in their tabs is not its
  // business, and vice versa.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== CONSUMER_TOKEN_KEY) return;
      if (isStaffSession()) return;

      if (!getToken()) {
        if (!authedRef.current) return;
        setConsumer(null);
        setAuthed(false);
        setSessionEndsAt(null);
        setExpired(false);
        setSignedOutElsewhere(true);
        return;
      }
      if (e.key === null || isGuestToken()) return;

      setConsumer(null);
      setAuthed(false);
      setSessionEndsAt(null);
      setSignedOutElsewhere(false);
      refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // The server's session end arrives → sign out on screen too, refresh or not,
  // with the login screen's "your session ended" note. The token is already dead
  // on the server at that moment, so there is nothing to revoke.
  useEffect(() => {
    if (!authed || !sessionEndsAt) return undefined;
    const timer = setTimeout(() => {
      clearToken();
      setConsumer(null);
      setAuthed(false);
      setSessionEndsAt(null);
      setExpired(true);
    }, Math.max(0, sessionEndsAt - Date.now()));
    return () => clearTimeout(timer);
  }, [authed, sessionEndsAt]);

  const signInWithToken = (token, consumerObj) => {
    setToken(token);
    setConsumer(consumerObj || null);
    setAuthed(true);
    setExpired(false);            // they're back in; the notice must not linger
    setSignedOutElsewhere(false);
    // Always ask /me: it carries the profile AND when this new session ends.
    refresh();
  };

  const signOut = async () => {
    try { await consumerApi.logout(); } catch { /* ignore */ }
    clearToken();
    setConsumer(null);
    setAuthed(false);
    // Deliberate sign-out is not an expired session — don't tell them their
    // session ran out when they pressed Log out themselves.
    setExpired(false);
  };

  // A guest (free plan, no signup) is NOT authenticated — no user chip, no
  // dashboard menu. Reads the server's is_guest flag (works for mobile + email).
  const isGuest = isGuestConsumer(consumer);

  return (
    <AuthCtx.Provider value={{ consumer, loading, isAuthed: authed && !isGuest, isGuest, expired, signedOutElsewhere, refresh, signInWithToken, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useConsumerAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useConsumerAuth must be used within ConsumerAuthProvider');
  return ctx;
};
